import { FC, ReactNode } from 'react';
import Slider from 'rc-slider';
import theme from 'config/theme';
import { Link } from 'components/Link';
import { getCommunityImageUrl } from 'helpers/imageUrls';
import { ButtonBrand } from 'components/ButtonBrand';
import { formatTime } from 'helpers/date';
import { trackEvent } from 'helpers/mixpanelEvents';
import {
  getUrlParams,
  abbreviateNum,
  codeFormatter,
  getPathWithRef,
  limitChars,
  sanitizeHtml,
} from 'helpers/utils';
import { IPage } from 'store/donatePage/types';
import { useAppSelector } from 'helpers/hooks';
import { getSetsData } from 'selectors/sets';
import { Image } from 'components/Image';
import classNames from 'classnames';

interface Props {
  allowCardMaxWidth?: boolean;
  issue: IPage;
  width?: string;
  isBottomFaded?: boolean;
  children?: ReactNode;
  fullHeight?: boolean;
}

const sliderStyles = {
  rail: {
    height: 12,
    borderRadius: 12,
  },
  track: {
    background: `linear-gradient(to right, ${theme.colors.darkGreen}, ${theme.colors.oathBlue})`,
    height: 12,
  },
  handle: {
    display: 'none',
  },
};

export const TopIssueCard: FC<Props> = ({
  allowCardMaxWidth = true,
  issue,
  width,
  isBottomFaded,
  children,
  fullHeight,
}) => {
  const setsData = useAppSelector(getSetsData);
  const primaryColor = setsData?.set_misc?.primary_color || theme.colors.inkBlue;

  const handleDonate = () => {
    // Read at fire time, not render time — the card outlives any one URL.
    const { urlPartner } = getUrlParams();
    trackEvent('Top Issue Donate Button Clicked', {
      tag_id: issue.id,
      set_query_str: urlPartner,
    });
  };

  return (
    <div
      className="top-issue-wrapper"
      style={{ width: width || 'auto', height: fullHeight ? 'auto' : 'fit-content' }}
    >
      <div
        className={classNames('top-issue-container', {
          'small-card': allowCardMaxWidth && !width,
          'full-height': fullHeight,
        })}
        style={{ borderColor: setsData?.set_misc?.issue_misc?.edge_color || theme.colors.inkBlue }}
      >
        <div className="column p16">
          <span className="eyebrow-text mb4" style={{ color: primaryColor }}>
            {issue.page_category || 'TOP ISSUE'}
          </span>
          <div className="row">
            {issue?.has_community_image && (
              <Image
                alt={`${issue.value} graphic`}
                src={getCommunityImageUrl(issue)}
                className="top-issue-image mr8"
              />
            )}
            <span className="h3-mobile" style={{ color: primaryColor }}>
              {issue.value}
            </span>
          </div>
          {issue?.display_fundraising_bar && (
            <>
              <div className="row justify-between items-end mt16">
                <span className="body-text" style={{ color: primaryColor }}>
                  ${abbreviateNum(parseInt(issue.raised))}{' '}
                  {setsData?.set_misc?.issue_misc?.card_raised_alt || 'Raised'}
                </span>
                <span className="body-text" style={{ color: primaryColor }}>
                  {setsData?.set_misc?.issue_misc?.card_goal_alt || 'Goal'} $
                  {abbreviateNum(parseInt(issue.fundraising_goal))}
                </span>
              </div>
              <Slider
                min={0}
                max={100}
                value={(parseInt(issue.raised) * 100) / parseInt(issue.fundraising_goal)}
                styles={sliderStyles}
                className="mb10"
              />
              <span className="utility-text" style={{ color: primaryColor }}>
                Updated at {formatTime(new Date())}
              </span>
            </>
          )}
          {children ? (
            <>{children}</>
          ) : (
            <>
              <ButtonBrand
                onClick={handleDonate}
                href={getPathWithRef(`/donate?p=${issue.query_str}`)}
                target="_blank"
                className="mt16 mb16"
                $backgroundColor={
                  setsData?.set_misc?.issue_misc?.cta_color || theme.colors.oathBlue
                }
                $gradientColor={setsData?.set_misc?.tertiary_color || theme.colors.oathBlue}
              >
                Donate
              </ButtonBrand>
              {!!issue.page_cta && (
                <>
                  <div className="line" />
                  <span className="eyebrow-text color-inkBlue mb4">
                    {setsData?.set_misc?.issue_misc?.card_helper_text || 'WHY THIS MATTERS'}
                  </span>
                  <span
                    className="body-text mb4"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(codeFormatter(limitChars(issue.page_cta, 155))),
                    }}
                  />
                </>
              )}
              <Link
                href={getPathWithRef(issue.learn_more_link || `/donate?p=${issue.query_str}`)}
                target="_blank"
                className="mb8"
                $color={primaryColor}
                $hoverColor={setsData?.set_misc?.tertiary_color || theme.colors.oathBlue}
              >
                <span className="body-small-text color-inherit">
                  {issue?.bottom_url_text ?? 'Learn More'}
                </span>
              </Link>
            </>
          )}
        </div>
        {isBottomFaded && <div className="fade-wrapper" />}
      </div>
    </div>
  );
};
